import React, { useRef, useState, useEffect, useMemo } from 'react'

import { useLocation, useParams } from 'react-router-dom'
import { Card, Person, Button, TextArea, Avatar } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import messages from './messages'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import useApi from 'hooks/useApi'

const CustomerFeedbackPage = ({ history }) => {
  const { formatMessage } = useIntl()
  const { hash } = useParams()
  const { search } = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  const token = searchParams.get('token')
  const firstRating = searchParams.get('rating')

  if (!hash) {
    history.push('/home')
  }

  const chatRef = useRef()
  const url = hash && token ? `/rest/v1/order/${hash}/feedback?token=${token}` : `/rest/v1/order/${hash}/feedback`
  const [data, loading, errors, refetch] = useApi(url)

  const [questions, setQuestions] = useState([])
  const { order, contact, customer, questions: questionList } = data || {}

  const questionsIndex = useMemo(() => {
    const index = questions.findIndex((q) => !q.rating)

    return index > -1 ? index : questions.length
  }, [questions])

  useEffect(() => {
    chatRef?.current.scrollTo({
      left: 0,
      top: chatRef?.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [questionsIndex])

  useEffect(() => {
    setQuestions(questionList || [])
    questionList?.map((question) => {
      if (question.key == 'experience') {
        onChange(question, { rating: firstRating })
      }
    })
  }, [questionList])

  const onChange = (prevQuestion, change) => {
    const question = { ...prevQuestion, ...change }

    fetch(url, {
      method: 'PATCH',
      body: JSON.stringify({
        id: question.id,
        key: question.key,
        note: question.note,
        rating: question.rating,
      })
    }).then((response) => response.json())
      .then((response) => console.log(response))

    setQuestions((prev) => prev.map((q) => 
      q.key === question.key ? question : q
    ))
  }

  const onSubmit = (e) => {
    e.preventDefault()

    fetch(`/rest/v1/order/${hash}/submit-feedback?token=${token}`, {
      method: 'PATCH',
      body: JSON.stringify(
        questions.filter((question) => !question.submitted_at && question.rating)
      )
    }).then((response) => response.json())
      .then(({data}) => {
        data.questions.some((question) => !question.submitted_at) ?
          toastr.success(formatMessage(messages.feedbackPartiallySubmitted)) : 
          toastr.success(formatMessage(messages.feedbackFullySubmitted))
        history.push('/home')
      })
  }

  return (
    <main className="w-full h-full overflow-scroll " ref={chatRef}>
      <form className="m-auto max-w-2xl p-8 space-y-20" onSubmit={onSubmit}>
        <div className="space-y-4">
          <div className="">
            <span className="group rounded-full border px-2 py-0.5 font-medium space-x-1 text-xs bg-gray-100 border-gray-100 text-gray-600">
              Order ID #{order?.uid}
            </span>
          </div>

          <h1 className="font-medium text-3xl">How did we do, on your latest order?</h1>

          <Card className="bg-gray-50 p-4 divide-y divide-gray-300">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium">{ order?.name }</h3>
                <h3 className="text-sm">{ customer?.name }</h3>
                <small className="text-xs">
                  {order &&
                    <FormattedDate value={new Date(order?.deadline * 1000)} />
                  }
                </small>
              </div>

              { contact?.id &&
                <div>
                  <Person {...contact} />
                </div>
              }
            </div>
          </Card>

          <p className="text-sm text-grayscale-lighter">Please help us improve, by answering a few question below.</p>
        </div>

        <div className="space-y-6">
          { questions.filter((_, i) => i <= questionsIndex).map((question, i) => (
            <fieldset key={question.key} className="space-y-10">
              <div className="flex items-center space-x-4">
                { question.person && 
                  <Avatar src={question.person.image} />
                }

                <div>
                  { question.person &&
                  <small className="block text-xs text-gray-600 font-medium">
                    <span>{ question.person.displayName || question.person.name }</span>
                    { !!question.person.title?.length &&
                      <span className="font-n?ormal">, { question.person.title }</span>
                    }
                  </small>
                  }
                  <label className="inline-block text-sm py-2 px-4 rounded bg-primary text-white w-auto">
                    <FormattedMessage {...messages[question.label]} />
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-2">
                <div className="w-2/3">
                  <TextArea
                    disabled={question.submitted_at}
                    className="w-full rounded"
                    placeholder={formatMessage(messages[question.placeholder])}
                    defaultValue={question.note}
                    onBlur={({ target: { value } }) => onChange(question, { note: value })}
                  />

                  <div className="flex space-x-1 items-center">
                    {[...Array(5)].map((_, i) => (
                      <Button
                        disabled={question.submitted_at}
                        key={`${question.key}-${i}`}
                        variant={(i + 1) <= (question?.rating || 0) ? 'solid' : 'outline'}
                        size="xs"
                        state="positive"
                        onClick={() => onChange(question, { rating: (i + 1) })}
                      >
                        {i + 1}
                      </Button>
                    )) }

                    <label className="text-sm text-gray-400">stars <Icon icon="star" className="text-positive" /></label>
                  </div>
                </div>

                { contact?.id ? (
                  <Avatar src={contact.image} />
                ) : (
                  <Avatar />
                ) }

              </div>
            </fieldset>
          ))}
        </div>

        { questionsIndex > 0 &&
          <div className="flex items-center justify-between">
            <p>{ questionsIndex } of { questions.length }</p>

            <Button
              size="sm"
              type="submit"
              disabled={!questions.some((question) => !question.submitted_at)}
            >
              Submit
            </Button>
          </div>
        }
      </form>
    </main>
  )
}

export default CustomerFeedbackPage
